<template>
	<div class="project-detail-route">
	<article class="project-detail" v-if="isReady">
		<header class="project-detail-header" v-if="!compactHeader">
			<div class="project-heading-copy">
				<div class="project-source" v-if="project.proposalId">
					{{ $t('projects.from') }}
					<strong>{{ project.proposalName ? project.proposalName : $t('projects.project') }}</strong>
				</div>
				<div class="project-title-row">
					<h1>{{ project.name }}</h1>
					<portal-status-pill
						:background="project.active ? 'var(--v-success_10-base)' : 'var(--v-gray_20-base)'"
						:color="project.active ? 'var(--v-success-base)' : 'var(--portal-muted)'"
					>
						<portal-icon :name="isProjectSuspended(project) ? 'folder-kanban' : project.active ? 'folder-kanban' : 'circle-check'" :size="15"></portal-icon>
						{{ $t(projectStatusKey(project)) }}
					</portal-status-pill>
				</div>
			</div>
			<div class="project-due">
				<portal-icon name="calendar-days" :size="17"></portal-icon>
				<span>{{ $t('global.due') }}</span>
				<strong>{{ formattedDueDate }}</strong>
			</div>
		</header>

		<section class="project-description" v-if="project.description">
			<div v-html="project.description"></div>
		</section>

		<time-and-money :project="project"></time-and-money>

		<section class="project-work" v-if="pendingDeliverables.length || previousDeliverables.length">
			<h2 class="section-title">{{ $t('projects.client-workflow') }}</h2>
			<div class="approval-grid">
				<div class="work-column">
					<div class="work-group" v-for="pending in pendingDeliverables" :key="pending.statusId">
						<h3>{{ pending.label }}</h3>
						<div class="deliverable-grid">
							<deliverable-card
								v-for="deliverable in pending.deliverables"
								:key="deliverable.id"
								:status-list="statusList"
								:deliverable="deliverable"
								@open-deliverable="openDeliverable(deliverable)"
							></deliverable-card>
						</div>
					</div>
				</div>
				<div class="work-column">
					<div class="work-group" v-for="previous in previousDeliverables" :key="previous.statusId">
						<h3>{{ previous.label }}</h3>
						<div class="deliverable-grid">
							<deliverable-card
								v-for="deliverable in previous.deliverables"
								:key="deliverable.id"
								:status-list="statusList"
								:deliverable="deliverable"
								@open-deliverable="openDeliverable(deliverable)"
							></deliverable-card>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="project-files">
			<h2 class="section-title">{{ $t('projects.attachments') }}</h2>
			<domain-root
				type="Project"
				:id="project.id"
				:show-deleted="showDeleted"
				:read-only="!canEditProject(project)"
			></domain-root>
		</section>
	</article>
	<div class="project-detail-loading" v-else>
		<v-progress-circular indeterminate :size="32" :width="3" color="primary"></v-progress-circular>
	</div>
	</div>
</template>

<script>
	import { isProjectSuspended, canEditProject, projectStatusKey } from '@/modules/projects/projectLifecycle';

	import { DateTime } from 'luxon';
	import ProjectService from '@/modules/projects/ProjectService';
	import DeliverableCard from '@/modules/projects/DeliverableCard';
  import DeliverableDetail from '@/modules/projects/deliverable/DeliverableDetail';
  import TimeAndMoney from '@/modules/projects/TimeAndMoney';
  import DomainRoot from '@/modules/files/DomainRoot';

	export default {
		name: 'Project',

	props: {
		project: { type: Object, required: true },
		statusList: { type: Array, required: false, default: () => [] },
		compactHeader: { type: Boolean, required: false, default: false },
	},

		components: { DomainRoot, TimeAndMoney, DeliverableCard },

		data: function() {
			return {
        showDeleted: false,
				DateTime: DateTime,
				projectService: new ProjectService(),
				deliverables: null,
        isReady: false,
			};
		},

		mounted() {
      this.$store.state.eventBus.$on('file-system-toggle-deleted', this.toggleShowDeleted);
			this.getDeliverables();
		},

		beforeDestroy() {
      this.$store.state.eventBus.$off('file-system-toggle-deleted', this.toggleShowDeleted);
    },

		methods: {
			isProjectSuspended, canEditProject, projectStatusKey,
			getDeliverables: function() {
				this.projectService.getProjectDeliverables(this.project.id).then((res) => {
          this.deliverables = res.data;
				}).finally(() => this.isReady = true);
			},

      toggleShowDeleted: function(){
        this.showDeleted = !this.showDeleted;
      },

			openDeliverable: function(deliverable) {
        let binding = {
          origDeliverable: deliverable,
          project: this.project,
          statusList: this.statusList,
          subtasks: deliverable.subtasks || []
        }

        this.$store.state.globalModalController
            .openModal(DeliverableDetail, binding,true,false,'right-panel')
            .then(() => this.getDeliverables());
      },

			openProposal: function() {

      },

      populateMap: function(deliverables){
        let result = new Map();
        deliverables.forEach(d => {
          if (!result.has(d.statusId)) {
            result.set(d.statusId, [])
          }
          result.get(d.statusId).push(d);
        });
        return result;
      },

      organizeDeliverables(map){
        let result = [];
        for (let statusId of map.keys()) {
          let statusIx = this.statusList.findIndex(s => s.id === statusId);
          let status = this.statusList[statusIx];
          result.push({
            statusId:statusId,
            label: status ? status.label : this.$t('projects.no-status'),
            sort: statusIx > -1 ? statusIx : Number.MAX_SAFE_INTEGER,
            deliverables: map.get(statusId)
          });
        }
        result.sort((a,b) => a.sort-b.sort);
        return result;
      }
		},

		computed: {
      pendingDeliverables: function(){
        if(this.deliverables){
          let map = this.populateMap(this.deliverables.pendingApproval);
          return this.organizeDeliverables(map);
        }else{
          return [];
        }
      },

      previousDeliverables: function(){
        if(this.deliverables){
          let map = this.populateMap(this.deliverables.previouslyApproved);
          return this.organizeDeliverables(map);
        }else{
          return [];
        }
      },

			formattedDueDate: function() {
				if (this.project.dueDate) {
					return DateTime.fromISO(this.project.dueDate).toLocaleString(DateTime.DATE_MED);
				} else {
					return this.$t('projects.no-due-date');
				}
			},

			formattedFees: function() {
				if (this.project.feeSchedule) {
					let amount = 0;

					if (this.project.feeSchedule.amount) {
						amount = this.$formatters.dollars(this.project.feeSchedule.amount, false);
					}

					switch (this.project.feeSchedule.feeType) {
						case 'Hourly': {
							return amount + '/hr';
						}

						case 'Fixed Price': {
							return amount + '/' + this.$t('projects.fixed');
						}

						case 'Retainer': {
							if (this.project.feeSchedule.retainerSchedule) {
								return amount + '/' + this.project.feeSchedule.retainerSchedule.toLowerCase();
							} else {
								return amount + '/?';
							}
						}

						default: {
							return '<span class="font-gray_70">--</span>';
						}
					}
				} else {
					return '<span class="font-gray_70">--</span>';
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	.project-detail-route {
		min-height: 320px;
	}

	.project-detail-loading {
		min-height: 320px;
		display: grid;
		place-items: center;
	}

	.project-detail {
		padding: clamp(18px, 3vw, 30px);
	}

	.project-detail-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 20px;
		padding-bottom: 22px;
		border-bottom: 1px solid var(--portal-border);
	}

	.project-heading-copy {
		min-width: 0;
	}

	.project-source {
		margin-bottom: 7px;
		color: var(--portal-muted);
		font-size: 12px;
	}

	.project-source strong {
		color: var(--portal-text);
		font-weight: 600;
	}

	.project-title-row {
		display: flex;
		align-items: center;
		gap: 10px;
		flex-wrap: wrap;
	}

	h1 {
		margin: 0;
		color: var(--portal-text);
		font-size: clamp(22px, 2.4vw, 30px);
		font-weight: 680;
		line-height: 1.15;
		letter-spacing: -0.025em;
	}

	.project-due {
		flex: 0 0 auto;
		display: grid;
		grid-template-columns: auto auto;
		align-items: center;
		column-gap: 7px;
		row-gap: 2px;
		padding: 9px 11px;
		border-radius: var(--brand-surface-radius, 12px);
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
		font-size: 12px;
	}

	.project-due strong {
		grid-column: 2;
		color: var(--portal-text);
		font-size: 13px;
		font-weight: 650;
	}

	.project-description {
		max-width: 760px;
		padding: 22px 0 4px;
		color: var(--portal-muted);
		font-size: 14px;
		line-height: 1.65;
		text-align: left;

		::v-deep img {
			display: block;
			max-width: 100%;
			height: auto;
		}
	}

	.project-work {
		margin-top: 24px;
		padding-top: 24px;
		border-top: 1px solid var(--portal-border);
	}

	.approval-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 18px;
	}

	.all-project-work {
		margin-top: 24px;
		padding-top: 24px;
		border-top: 1px solid var(--portal-border);
	}

	.section-title {
		margin: 0 0 14px;
		color: var(--portal-text);
		font-size: 16px;
		font-weight: 680;
	}

	.work-status-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 16px;
	}

	.work-group {
		min-width: 0;
		padding: 12px;
		border-radius: var(--brand-surface-radius, 14px);
		background: var(--portal-canvas);
	}

	.work-group-heading {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 10px;
	}

	.work-group-heading h3 {
		min-width: 0;
		flex: 1;
		margin: 0;
		overflow: hidden;
		color: var(--portal-text);
		font-size: 13px;
		font-weight: 650;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.work-group-heading > span:last-child {
		min-width: 22px;
		padding: 2px 6px;
		border-radius: 99px;
		background: var(--portal-surface);
		color: var(--portal-muted);
		font-size: 11px;
		text-align: center;
	}

	.status-dot {
		width: 8px;
		height: 8px;
		flex: 0 0 8px;
		border-radius: 50%;
	}

	.work-column,
	.work-group {
		min-width: 0;
	}

	.work-group + .work-group {
		margin-top: 20px;
	}

	.work-group h3 {
		margin: 0 0 10px;
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 650;
	}

	.deliverable-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: 8px;
	}

	.project-files {
		margin-top: 24px;
		padding-top: 24px;
		border-top: 1px solid var(--portal-border);
	}

	@media (max-width: 760px) {
		.project-detail-header {
			flex-direction: column;
		}

		.project-due {
			width: 100%;
		}

		.approval-grid {
			grid-template-columns: minmax(0, 1fr);
		}

		.work-status-grid {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>
